import type { SecLoansApplicationSelectData } from '@sec-loans-ui/common/types'
import { SecLoansAnalyticsService } from '@sec-loans-ui/services/analyticsService'
import { useAnalyticsService } from '@sec-loans-ui/state/analyticsContext'
import { SecLoansCreateApplicationRequest } from '@shared/sec-loans'
import { getOpenErrorPromptModalEvent } from '@ui-common/components/Modal'
import { usePersistingActor } from '@ui-common/hooks'
import { GlobalServicesContext } from '@ui-common/state'
import { useSelector } from '@xstate/react'
import { use } from 'react'
import { useIntl } from 'react-intl'
import { ContextFrom, StateValueFrom } from 'xstate'

import { createNewLoanApplicationMachine, NewLoanApplicationMachineType } from './newLoanApplicationMachine'

interface UseNewLoanApplicationMachineLogicReturn {
  submit: (data: SecLoansCreateApplicationRequest) => void
  stateValue: StateValueFrom<NewLoanApplicationMachineType>
  createdApplication: ContextFrom<NewLoanApplicationMachineType>['response']
  openErrorModal: () => void
  analyticsService: SecLoansAnalyticsService
}

export const useNewLoanApplicationMachineLogic = (): UseNewLoanApplicationMachineLogicReturn => {
  const intl = useIntl()
  const machine = createNewLoanApplicationMachine()
  const actor = usePersistingActor<NewLoanApplicationMachineType>(machine)
  const { analyticsService } = useAnalyticsService()
  const { modalService } = use(GlobalServicesContext)

  const openErrorModal = () => {
    analyticsService.sendModalViewEvent('ErrorPromptModal')
    modalService.send(
      getOpenErrorPromptModalEvent(() => {
        actor.send({ type: 'PROMPT_CONFIRMED' })
        analyticsService.sendModalCustomEvent('ErrorPromptModal', 'ConfirmButton')
      }, intl),
    )
  }

  const {
    value,
    context: { response: createdApplication },
  } = useSelector(actor, (s) => s)
  return {
    submit: (data: SecLoansApplicationSelectData) => actor.send({ type: 'SUBMIT', data }),
    stateValue: value,
    createdApplication,
    openErrorModal,
    analyticsService,
  }
}
